// Thin TS wrapper over @platform/wiservice. wsInit() must run BEFORE the
// first ws() call (main.tsx does, before createRoot().render()). Don't catch
// auth errors here — the package auto-redirects on 103/239/250/501 unless a
// callback overrides it (see AccountService.sessionIsVerified).

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — the package ships .js without .d.ts; treated as `any` upstream.
import wiserviceImport from '@platform/wiservice';
import {
  Constants,
  APPNAME,
  WS_STATUS_OK,
  LOGIN_FLOW_VERSION_NEW,
} from '@/config/constants';

// Untyped JS package — minimal shape declared inline. Consumers don't need
// the full surface, just `setOptions` + `call`.
interface WiserviceOptions {
  ws_url?: string;
  account_domain?: string;
  appname?: string;
  apppfm?: string;
  appliver?: string;
  url_part?: string;
  sessionid?: string;
  rWithInvalidParams?: boolean;
}
interface WiserviceCallOptions {
  ws_url?: string;
  build_query?: boolean;
  timeout?: number;
}
interface WiserviceModule {
  setOptions(options: WiserviceOptions): void;
  call<T = unknown>(
    service: string,
    params?: Record<string, unknown>,
    options?: WiserviceCallOptions,
    callback?: ((data: WsResponse<unknown>) => void) | null,
  ): Promise<WsResponse<T>>;
  list(): Array<{ id: number; url: string }>;
  stop(id?: number): void;
}

const wiservice = wiserviceImport as unknown as WiserviceModule;

// ── Types exposed to callers ────────────────────────────────────────────────

export interface WsResponse<T = unknown> {
  status: number;
  body: T;
  error?: string;
}

/**
 * Thrown by `ws()` when the response carries a non-zero, non-auth status code
 * (e.g. 503 invalid params, business-level failures). Auth-error statuses
 * (103/239/250/501) don't reach this throw — the package auto-redirects
 * before the promise rejects, OR our callback intercepts (sessionIsVerified
 * path, in which case we throw `WsError` ourselves so the caller can branch).
 */
export class WsError extends Error {
  constructor(public readonly status: number, message?: string) {
    super(message ?? `Withings WS error (status ${status})`);
    this.name = 'WsError';
  }
}

// ── Boot init ───────────────────────────────────────────────────────────────

/**
 * Configure the underlying @platform/wiservice singleton with our
 * environment + branding. MUST be called once at app boot, before any ws()
 * call. We call it from `main.tsx` so React's first render finds the
 * package fully wired.
 *
 * The `url_part` template determines the redirect URL the package builds on
 * an auth error: `${account_domain}${workflow}/${block}${url_part}<encoded_return_url>`.
 * We bake `logver` + `appname` into it so the portal renders the right flow.
 */
export function wsInit(): void {
  const urlParams = new URLSearchParams(window.location.search);

  // Local-only escape hatch: `?sessionid=<key>` in the URL forces the package
  // to send that sessionid in every body (and skip the cookie path). Useful
  // when developing on raw localhost without the corp vhost.
  const sessionidOverride = Constants.ENV === 'local'
    ? urlParams.get('sessionid') ?? undefined
    : undefined;

  wiservice.setOptions({
    ws_url: Constants.SERVICE_URL,
    account_domain: Constants.ACCOUNT_URL,
    appname: APPNAME,
    apppfm: 'web',
    // `local` reports a fixed literal so dev-server requests are easy to
    // filter out in scalews logs. dev/beta/prod report the short commit
    // hash baked in at build time (see vite.config.ts `define`).
    appliver: Constants.ENV === 'local' ? 'localversion' : __COMMIT_HASH__,
    url_part: `?logver=${LOGIN_FLOW_VERSION_NEW}&appname=${APPNAME}&r=`,
    ...(sessionidOverride ? { sessionid: sessionidOverride } : {}),
  });
}

// ── Per-call wrapper ────────────────────────────────────────────────────────

export interface WsOptions {
  /**
   * AbortSignal-driven request timeout in ms. Unset by default — we rely on
   * the scalews server-side timeout (and the browser's TCP timeout) as the
   * authority. Set per-call only if a specific endpoint warrants a hard
   * client-side cap (e.g. a healthcheck where you'd rather fail fast than
   * wait the server out).
   */
  timeout?: number;
  /**
   * Per-call callback invoked by the package on auth-error statuses (103/
   * 239/250/501) and on 503 — INSTEAD OF auto-redirecting. The package
   * resolves the promise with the `data` envelope when a callback is set,
   * so the caller can inspect `data.status` and decide. Use sparingly:
   * the only legit caller today is `AccountService.sessionIsVerified`.
   */
  onAuthInterrupt?: (data: WsResponse) => void;
}

/**
 * Call a Withings webservice action through @platform/wiservice.
 *
 * @param service  Service name (e.g. 'account', 'session', 'program').
 * @param action   Action under the service (e.g. 'get', 'isverified').
 * @param params   Action-specific parameters (sent in the POST body).
 * @param v1       If true, hit `${SERVICE_URL}${service}` (legacy CGI shape).
 *                 Otherwise the package prefixes with `v2/`.
 * @param options  Per-request overrides ({ timeout, onAuthInterrupt }).
 *
 * @returns The full envelope `{status, body}`.
 * @throws  `WsError` when the response carries a non-OK status the package
 *          re-threw (default branch of its switch) or when the
 *          `onAuthInterrupt` callback was used and the status was non-zero
 *          (we re-throw so the caller's try/catch fires).
 */
export async function ws<T = Record<string, unknown>>(
  service: string,
  action: string,
  params: Record<string, unknown> = {},
  v1: boolean = false,
  options: WsOptions = {},
): Promise<WsResponse<T>> {
  const path = v1 ? service : `v2/${service}`;

  // The package intercepts auth errors when a callback is provided. We use
  // it as a flag — when caller set `onAuthInterrupt`, we hand the package a
  // function that lets the promise resolve with the data envelope so we can
  // inspect status without a redirect.
  const callback = options.onAuthInterrupt
    ? (data: WsResponse) => options.onAuthInterrupt!(data)
    : null;

  let data: WsResponse<T>;
  try {
    data = await wiservice.call<T>(
      path,
      { ...params, action },
      // No default timeout: the package only triggers `AbortController.abort`
      // when `timeout` is truthy. Leaving it undefined lets the request run
      // until scalews answers or the TCP connection drops — which is what
      // wpc-front (older wiservice without a default timeout) does.
      { timeout: options.timeout },
      callback,
    );
  } catch (e) {
    // The package rejects a non-auth non-OK WS status with `new Error(String(status))`
    // — a plain Error whose message IS the WS code (apiv2 returns it in a 200
    // body, not an HTTP code). Normalize to WsError so callers branch on `.status`.
    const status = Number((e as Error)?.message);
    if (Number.isFinite(status) && status !== WS_STATUS_OK) throw new WsError(status);
    throw e;
  }

  // When `onAuthInterrupt` is set the package may RESOLVE with a non-OK status
  // (rather than reject). Re-throw as WsError so the caller's try/catch handles
  // it — same shape as the reject path normalized above.
  if (data && typeof data === 'object' && data.status !== WS_STATUS_OK) {
    throw new WsError(data.status, data.error);
  }

  return data;
}
